import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { formatValue, VALUE_COUNT } from "../utils/formatValue";
import { formatDays, measureLabel } from "../utils/measures";

/**
 * Right panel: Excel-styled pivot grid rendered from the pivot model
 * produced by computePivot().
 *
 * A measure pivot (pivot.measure) shows days per cell plus how many reports
 * fed each value (n); value modes do not apply to it.
 *
 * `bare` renders just the table, without the builder-panel card (border +
 * padding). For hosts that already draw their own card — a dashboard piece —
 * where the extra card showed up as a second border between title and table.
 */
export const PivotGrid = ({ pivot, fieldTitle, valueMode = VALUE_COUNT, bare = false }) => {
  const [t] = useTranslation("exports");

  if (!pivot) {
    return (
      <Box className="builder-panel builder-panel--hint dyTheme1 dyBorder1 rounded">
        <Typography className="builder-empty">
          {t("builder.apply_hint")}
        </Typography>
      </Box>
    );
  }

  const {
    rowFields,
    rowKeys,
    colKeys,
    cells,
    rowTotals,
    colTotals,
    grandTotal,
    isAllRow,
    isAllCol,
  } = pivot;

  const { measure, counts } = pivot;

  // Empty cells stay blank; everything else is formatted against the grand total.
  const fmt = (v) => (v ? formatValue(v, grandTotal, valueMode) : "");
  const colHeader = (col) => (isAllCol ? measureLabel(t, measure) : col.values.join(" ▸ "));

  // One renderer per value slot. `n` is the report count behind a measure value.
  const show = (value, n) => {
    if (!measure) return fmt(value || 0);
    if (value == null) return "";
    return (
      <>
        {formatDays(value)}
        <span className="pivot-cell__n">n={n}</span>
      </>
    );
  };
  const cell = (rk, ck) => show(cells.get(`${rk}||${ck}`), counts?.cells.get(`${rk}||${ck}`));
  const rowTotal = (rk) => show(rowTotals.get(rk), counts?.rowTotals.get(rk));
  const colTotal = (ck) => show(colTotals.get(ck), counts?.colTotals.get(ck));

  const grid = (
    <div className="pivot-grid">
      <table className="pivot-grid__table">
        <thead>
          <tr>
            {/* corner: one header per row field */}
            {isAllRow ? (
              <th className="pivot-corner" />
            ) : (
              rowFields.map((f) => (
                <th key={f} className="pivot-corner">
                  {fieldTitle(f)}
                </th>
              ))
            )}
            {/* column headers */}
            {colKeys.map((col) => (
              <th key={col.key} className="pivot-colhead">
                {colHeader(col)}
              </th>
            ))}
            <th className="pivot-colhead pivot-total">{t("builder.total")}</th>
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((row) => (
            <tr key={row.key}>
              {isAllRow ? (
                <th className="pivot-rowhead">{t("builder.total")}</th>
              ) : (
                row.values.map((v, i) => (
                  <th key={i} className="pivot-rowhead">
                    {v}
                  </th>
                ))
              )}
              {colKeys.map((col) => (
                <td key={col.key} className="pivot-cell">
                  {cell(row.key, col.key)}
                </td>
              ))}
              <td className="pivot-cell pivot-total">
                {rowTotal(row.key)}
              </td>
            </tr>
          ))}
          {/* grand total row */}
          <tr>
            <th
              className="pivot-rowhead pivot-total"
              colSpan={isAllRow ? 1 : rowFields.length}
            >
              {t("builder.grand_total")}
            </th>
            {colKeys.map((col) => (
              <td key={col.key} className="pivot-cell pivot-total">
                {colTotal(col.key)}
              </td>
            ))}
            <td className="pivot-cell pivot-total pivot-total--grand">
              {measure
                ? show(grandTotal, counts?.grandTotal)
                : formatValue(grandTotal, grandTotal, valueMode)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  if (bare) return grid;
  return <Box className="builder-panel dyTheme1 dyBorder1 rounded">{grid}</Box>;
};
